import {
    api,
    apiErrorMessage,
    getTimezone,
    RecordPeek,
    todayInTz,
    useAuthStore,
} from '@bemor/web-kit';
import {
    AsOfBadge,
    AssistantButton,
    Button,
    CheckIcon,
    ConfirmDialog,
    DateTime,
    DateTimeField,
    Field,
    HideIcon,
    ObjectRef,
    PageHeader,
    RowActions,
    StatusBadge,
    zonedToIso,
    type ListColumnDef,
    type ListViewPreset,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { Alert } from '@/api/types';
import {
    AdminListPage,
    adminListPrefetch,
    filterDefs,
    useAdminList,
} from '@/lib/lists';
import { P } from '@/lib/permissions';
import { useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';

const PATH = '/staff/alerts';
const PAGE_KEY = 'alerts';

/** What still needs someone first (the old default: open), then everything not resolved, then all. */
export const ALERT_PRESETS: ListViewPreset[] = [
    { key: 'open', name: 'Open', filters: { state: ['open'] } },
    {
        key: 'active',
        name: 'Not resolved',
        filters: { state: ['open', 'acknowledged', 'silenced'] },
    },
    { key: 'all', name: 'All alerts', filters: {} },
];

/** The route's prefetch: the person's views, then the rows of the view they land on (BM-46 §2.0 + §2.4). */
export const alertsPrefetch = adminListPrefetch(PAGE_KEY, PATH, {
    presets: ALERT_PRESETS,
});

const alertFilters = filterDefs(PATH, {
    q: 'Search',
    state: 'State',
    severity: 'Severity',
    range: 'Raised',
});

export function AlertsPage() {
    const me = useMe();
    const can = useAuthStore((s) => s.can);
    const [silencing, setSilencing] = useState<Alert | null>(null);
    // BM-46 §2.6: a day and a time in the staff member's zone.
    const [until, setUntil] = useState<{
        day: string | null;
        time: string | null;
    }>({ day: null, time: null });
    const [error, setError] = useState<string | null>(null);
    const ack = useWrite(
        async (a: Alert) =>
            (
                await api().post<Alert>(`/staff/alerts/${a.id}/acknowledge`, {
                    version: a.version,
                })
            ).data,
        ['/staff/alerts', '/staff/queues'],
    );
    const silence = useWrite(
        async (body: { alert: Alert; until: string }) =>
            (
                await api().post<Alert>(
                    `/staff/alerts/${body.alert.id}/silence`,
                    { until: body.until },
                )
            ).data,
        ['/staff/alerts'],
    );
    const onError = (e: unknown) => setError(apiErrorMessage(e));
    const columns: ListColumnDef<Alert>[] = [
        {
            key: 'rule_key',
            aliases: ['rule'],
            label: 'Rule',
            sortKey: 'rule_key',
            boardCard: true,
            cell: (a) => (
                <span className="font-mono text-xs">{a.rule_key}</span>
            ),
            locked: 'start',
        },
        {
            key: 'severity',
            label: 'Severity',
            sortKey: 'severity',
            cell: (a) => <StatusBadge status={a.severity} domain="severity" />,
            groupable: { key: 'severity', statusDomain: 'severity' },
            boardCard: true,
        },
        {
            key: 'state',
            label: 'State',
            sortKey: 'state',
            cell: (a) => <StatusBadge status={a.state} domain="alert" />,
            groupable: { key: 'state', statusDomain: 'alert' },
        },
        {
            key: 'scope',
            label: 'Scope',
            sortKey: 'scope',
            cell: (a) =>
                `${a.scope}${a.merchant_id ? ` ${a.merchant_id}` : ''}`,
        },
        {
            key: 'observed',
            label: 'Observed',
            cell: (a) => (
                <code className="text-xs">
                    {Object.entries(a.observed)
                        .map(([k, v]) => `${k}=${String(v)}`)
                        .join(' ')}
                </code>
            ),
        },
        {
            key: 'raised_at',
            aliases: ['raised'],
            label: 'Raised',
            kind: 'datetime',
            sortKey: 'raised_at',
            cell: (a) => <DateTime value={a.raised_at} empty="—" />,
            noTruncate: true,
        },
        {
            key: 'acknowledged_at',
            aliases: ['ack'],
            label: 'Acknowledged',
            sortKey: 'acknowledged_at',
            cell: (a) =>
                a.acknowledged_at
                    ? `${who(a.acknowledged_by, me)}`
                    : a.silenced_until
                      ? 'silenced'
                      : '—',
        },
        {
            key: 'actions',
            label: 'Actions',
            locked: 'end',
            actions: true,
            // BM-84 (rule G.6, design 0.7.0 alerts): Acknowledge and Silence in one menu
            cell: (a) => (
                <RowActions
                    name={a.rule_key}
                    items={[
                        {
                            label: 'Acknowledge',
                            icon: CheckIcon,
                            hidden: !can(P.alertsAck) || a.state !== 'open',
                            onSelect: () => ack.mutate(a, { onError }),
                        },
                        {
                            label: 'Silence',
                            icon: HideIcon,
                            hidden:
                                !can(P.alertsAck) ||
                                a.state === 'resolved' ||
                                a.state === 'silenced',
                            onSelect: () => setSilencing(a),
                        },
                    ]}
                />
            ),
        },
    ];
    const { state, query } = useAdminList<Alert>(PAGE_KEY, PATH, {
        columns,
        filters: alertFilters,
        presets: ALERT_PRESETS,
    });
    const peeked = query.rows.find((a) => a.id === state.peek);
    return (
        <div>
            <PageHeader
                title="Alerts"
                meta={
                    <span className="flex flex-col gap-1">
                        <span>
                            {
                                "Raised by Reporting's rules; urgent ones also page. Acknowledge what you are on, silence what is known."
                            }
                        </span>
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </span>
                }
            />
            {error ? (
                <p role="alert" className="text-destructive mb-3 text-sm">
                    {error}
                </p>
            ) : null}
            <AdminListPage
                noun="alerts"
                state={state}
                query={query}
                rowKey={(a) => a.id}
                onRow={(a) => state.setPeek(a.id)}
                controls={{
                    q: { kind: 'search' },
                    state: { domain: 'alert' },
                    severity: { domain: 'severity' },
                }}
                empty={
                    <p className="text-muted-foreground p-6 text-center text-sm">
                        Nothing is alerting.
                    </p>
                }
            />
            <RecordPeek state={state} title={peeked?.rule_key ?? 'Alert'}>
                {() =>
                    peeked ? (
                        <div className="flex flex-col gap-4">
                            <dl
                                className="grid grid-cols-1 gap-3 text-sm"
                                data-testid="alert-peek"
                            >
                                <div>
                                    <dt className="text-muted-foreground">
                                        Rule
                                    </dt>
                                    <dd className="font-mono text-xs">
                                        {peeked.rule_key}
                                    </dd>
                                </div>
                                <div className="flex gap-2">
                                    <StatusBadge
                                        status={peeked.severity}
                                        domain="severity"
                                    />
                                    <StatusBadge
                                        status={peeked.state}
                                        domain="alert"
                                    />
                                </div>
                                <div>
                                    <dt className="text-muted-foreground">
                                        Scope
                                    </dt>
                                    <dd>
                                        {peeked.scope}
                                        {peeked.merchant_id ? (
                                            <>
                                                {' '}
                                                <ObjectRef
                                                    id={peeked.merchant_id}
                                                />
                                            </>
                                        ) : null}
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-muted-foreground">
                                        Observed
                                    </dt>
                                    <dd>
                                        <code className="text-xs break-all">
                                            {Object.entries(peeked.observed)
                                                .map(
                                                    ([k, v]) =>
                                                        `${k}=${String(v)}`,
                                                )
                                                .join(' ')}
                                        </code>
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-muted-foreground">
                                        Raised
                                    </dt>
                                    <dd>
                                        <DateTime
                                            value={peeked.raised_at}
                                            empty="—"
                                        />
                                    </dd>
                                </div>
                                <div>
                                    <dt className="text-muted-foreground">
                                        Acknowledged
                                    </dt>
                                    <dd>
                                        {peeked.acknowledged_at
                                            ? who(peeked.acknowledged_by, me)
                                            : peeked.silenced_until
                                              ? 'Silenced'
                                              : '—'}
                                    </dd>
                                </div>
                            </dl>
                            <AssistantButton
                                surface="alert"
                                subject={{ type: 'alert', id: peeked.id }}
                                className="mb-2"
                            />
                            {can(P.alertsAck) && peeked.state !== 'resolved' ? (
                                <div
                                    className="flex gap-2"
                                    data-testid="alert-peek-actions"
                                >
                                    {peeked.state === 'open' ? (
                                        <Button
                                            size="sm"
                                            onClick={() =>
                                                ack.mutate(peeked, { onError })
                                            }
                                        >
                                            Acknowledge
                                        </Button>
                                    ) : null}
                                    {peeked.state !== 'silenced' ? (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setSilencing(peeked)}
                                        >
                                            Silence
                                        </Button>
                                    ) : null}
                                </div>
                            ) : null}
                        </div>
                    ) : (
                        <p className="text-muted-foreground text-sm">
                            This alert is not on the current page.
                        </p>
                    )
                }
            </RecordPeek>
            <ConfirmDialog
                open={silencing !== null}
                onOpenChange={(o) => (o ? null : setSilencing(null))}
                title="Silence this alert"
                description="It stops paging until the time you give; it stays in the queue as silenced."
                confirmLabel="Silence"
                busy={silence.isPending}
                onConfirm={() => {
                    if (!silencing || !until.day) return;
                    silence.mutate(
                        {
                            alert: silencing,
                            until: zonedToIso(
                                until.day,
                                until.time ?? '00:00',
                                getTimezone(),
                            ),
                        },
                        { onSuccess: () => setSilencing(null), onError },
                    );
                }}
            >
                <Field htmlFor="silence_until" label="Until">
                    <DateTimeField
                        id="silence_until"
                        value={until}
                        min={todayInTz(getTimezone())}
                        onChange={setUntil}
                    />
                </Field>
            </ConfirmDialog>
        </div>
    );
}
